import { CommonModule } from '@angular/common';
import { NgModule, Pipe, PipeTransform } from '@angular/core';
import { RelationOperatorSearchText } from '@mtna/data-core-ts';

import { RdsChip } from '../../chips/chips.component';
import { RdsTextFilter } from '../../text-filter/text-filter.model';
import { BaseFilter } from '../models/filter/base-filter.model';
import { getRdsDateFilterIso, RdsDateFilter } from '../models/filter/date-filter.model';
import { instanceOfFilterByCodes } from '../models/filter/filter-by-codes.model';
import { instanceOfFilterByDate } from '../models/filter/filter-by-date.model';
import { instanceOfFilterByNumber } from '../models/filter/filter-by-number.model';
import { instanceOfFilterByText } from '../models/filter/filter-by-text.model';
import { RdsFilterType } from '../models/filter/filter-type.model';

/**
 * Turn `BaseFilter` into a summary object which holds the type and RdsChips to represent each filter
 *
 * @author Chase Moore <chase.moore@mtna.us>
 * @author Will Davis <will.davis@mtna.us>
 */
@Pipe({
  name: 'rdsFilterSummaryChips',
})
export class RdsFilterSummaryChipsPipe implements PipeTransform {
  transform(filter: BaseFilter): { filterType: RdsFilterType; chips: RdsChip[] } | null {
    let filterType: RdsFilterType | undefined;
    let chips: RdsChip[] = [];
    if (instanceOfFilterByCodes(filter)) {
      filterType = 'CODE';
      if (!!filter.codes && filter.codes.length) {
        chips = filter.codes.map((c) => new RdsChip(`${c.codeValue} : ${c.name}`, c.uri));
      }
    } else if (instanceOfFilterByNumber(filter)) {
      filterType = 'NUMERIC';
      if (!!filter.numericFilters && !!filter.numericFilters.length) {
        chips = filter.numericFilters.map(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (nf) => new RdsChip(`${RelationOperatorSearchText[nf.operation!.type]} ${nf.value}`, (nf.operation?.type || '') + nf.value)
        );
      }
    } else if (instanceOfFilterByText(filter)) {
      filterType = 'TEXT';
      if (!!filter.textFilters && !!filter.textFilters.length) {
        chips = filter.textFilters.map(
          (tf: RdsTextFilter) => new RdsChip(`${tf.parameter?.label} "${tf.text}"`, (tf.parameter?.type || '') + tf.text)
        );
      }
    } else if (instanceOfFilterByDate(filter)) {
      filterType = 'DATE';
      if (!!filter.dateFilters && !!filter.dateFilters.length) {
        chips = filter.dateFilters.map(
          (dateFilter: RdsDateFilter) =>
            new RdsChip(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              `${RelationOperatorSearchText[dateFilter.operation!.type]} ${getRdsDateFilterIso(dateFilter)}`,
              (dateFilter.operation?.type || '') + dateFilter.value?.valueOf()
            )
        );
      }
    }

    // Add null filter
    if (!!filter.nullFilter && filter.nullFilter.parameter?.type !== 'NOT_APPLIED') {
      chips.push(new RdsChip(filter.nullFilter.parameter.label, filter.nullFilter.parameter.type));
    }
    return filterType ? { filterType, chips } : null;
  }
}

@NgModule({
  imports: [CommonModule],
  exports: [RdsFilterSummaryChipsPipe],
  declarations: [RdsFilterSummaryChipsPipe],
})
export class RdsFilterSummaryChipsPipeModule {}
